import { IDAware } from '../helpers/interfaces/id-aware';
import { IDAwareNonOptional } from '../helpers/interfaces/id-aware-non-optional';
import { UtilityFunctions } from './utility-functions';

export class CollectionHelper {
  static isPopulated<T>(arr: T[]): boolean {
    return arr != null && arr.length > 0;
  }

  static hasLengthGreaterThan<T>(arr: T[], length: number): boolean {
    return arr != null && arr.length > length;
  }

  static filterInPlace<T>(
    array: T[],
    predicate: (e: T, ...args: any) => boolean,
    ...args: any
  ): void {
    if (array == null || array.length === 0) {
      return;
    }
    let iOut = 0;
    for (let i = 0; i < array.length; i++) {
      if (!this.isPopulated(args)) {
        if (predicate(array[i])) {
          array[iOut++] = array[i];
        }
      } else {
        if (predicate(array[i], args)) {
          array[iOut++] = array[i];
        }
      }
    }
    array.length = iOut;
  }

  /**
   * Remove all occurrences of elements in subtrahend from minuend
   */
  static diffInPlace<T>(minuend: T[], subtrahend: T[]): void {
    if (!this.isPopulated(minuend) || !this.isPopulated(subtrahend)) {
      return;
    }
    let iOut = 0;
    for (let i = 0; i < minuend.length; i++) {
      if (
        subtrahend.findIndex((s) =>
          UtilityFunctions.sameValueZero(minuend[i], s)
        ) < 0
      ) {
        minuend[iOut++] = minuend[i];
      }
    }
    minuend.length = iOut;
  }

  static count<T>(array: T[], predicate: (e: T) => boolean): number {
    if (array == null) {
      return null;
    }
    if (array.length === 0) {
      return 0;
    }
    let toReturn = 0;
    for (const e of array) {
      toReturn += predicate(e) ? 1 : 0;
    }
    return toReturn;
  }

  static countUpTo<T>(
    array: T[],
    predicate: (e: T) => boolean,
    upTo: number
  ): number {
    if (array == null || upTo == null || upTo < 0) {
      return null;
    }
    if (array.length === 0) {
      return 0;
    }
    let toReturn = 0;
    for (const e of array) {
      toReturn += predicate(e) ? 1 : 0;
      if (toReturn >= upTo) {
        break;
      }
    }
    return toReturn;
  }

  // Maybe generalize to arbitrary array depth, unlikely we'd ever need that however
  static some<T>(array: T[][], predicate: (e: T) => boolean): boolean {
    return array == null ? null : array.some((r) => r.some(predicate));
  }

  static every<T>(array: T[][], predicate: (e: T) => boolean): boolean {
    return array == null ? null : array.every((r) => r.every(predicate)); // consider eventual special behavior if we don't want to return true for empty arrays
  }

  static findMax<T>(array: T[], mapper: (e: T) => number): number {
    if (!this.isPopulated(array)) {
      return null;
    }
    let toReturn = -Infinity;
    for (const e of array) {
      toReturn = e == null ? toReturn : Math.max(toReturn, mapper(e));
    }
    return toReturn;
  }

  static findMin<T>(array: T[], mapper: (e: T) => number): number {
    if (!this.isPopulated(array)) {
      return null;
    }
    let toReturn = Infinity;
    for (const e of array) {
      toReturn = e == null ? toReturn : Math.min(toReturn, mapper(e));
    }
    return toReturn;
  }

  // if multiple elements are the maximum according to compare,
  // the first such element that occurs in the array is returned
  static findMaxWithCompare<T>(
    array: T[],
    compare: (e1: T, e2: T) => number
  ): T {
    if (!this.isPopulated(array)) {
      return null;
    }
    let toReturn = null;
    for (const e of array) {
      toReturn =
        toReturn == null ? e : UtilityFunctions.max(toReturn, e, compare);
    }
    return toReturn;
  }

  static toIdMap<T extends IDAware>(array: T[]): Map<number, T> {
    return array == null
      ? null
      : new Map(
          array.filter((e) => e != null && e.id != null).map((e) => [e.id, e])
        );
  }

  static toNonOptionalIdMap<T extends IDAwareNonOptional>(
    array: T[]
  ): Map<number, T> {
    return array == null
      ? null
      : new Map(array.filter((e) => e != null).map((e) => [e.id, e]));
  }

  static uniqInPlaceBy<T, U>(array: T[], key: (e: T) => U): void {
    if (array == null || array.length === 0) {
      return;
    }

    let iOut = 0;
    let seen = new Set<U>();
    for (let i = 0; i < array.length; i++) {
      const k = key(array[i]);
      if (!seen.has(k)) {
        array[iOut++] = array[i];
        seen.add(k);
      }
    }
    array.length = iOut;
  }

  static getNullArray<T>(length: number): T[] {
    const toReturn = Array(length);
    for (let i = 0; i < length; i++) {
      toReturn[i] = null;
    }
    return toReturn;
  }
}
